import _ from "lodash";
import React, { useState, useRef } from "react";

// Components
import ContextSelectionItem from "../ContextSelectionItem";

// DS
import { Modal, Button, Tooltip } from "@toddle-edu/ds-web";

// Utils
import { getFieldValue } from "Assessments/modules/Utils";
import {
  getModalTitle,
  getTypeToRender,
  getDependentFields,
  getFieldSelectionModalWidth,
  getFieldSelectionModalHeight,
  getCustomModalStyle,
} from "../../modules/Utils";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";

// third party libs.
import { connect } from "react-redux";

// Modules
import {
  updateAssignmentField,
  updateUnitPlanField,
} from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/LessonPlanningAssistantModule";

// constants
import { ELEMENT_TYPE_ADD_LEARNING_GOALS } from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/Constants";

// Scss
import classes from "./ContextSelectionModal.scss";

const styles = {
  headerWithoutSubtextStyle: {
    padding: "20px 24px 0 32px",
    alignItems: "center",
  },
  headerWithSubtextStyle: {
    padding: "20px 24px 0 32px",
    alignItems: "flex-start",
  },
};

const ContextSelectionModal = props => {
  const {
    t,
    fields,
    onClose,
    fieldList,
    isUnitDependent,
    allFields,
    modalTitle,
    assessmentDetails,
    portalType,
    updateAssignmentField,
    updateUnitPlanField,
    setContext,
    modalSubtext,
    modalStyle,
    firstFieldType,
    shouldRenderModal,
    unitPlanDetails,
    testId,
    isPlannerElementDataSet,
  } = props;

  const [updateParams, setUpdateParams] = useState({});
  const [localContext, setLocalContext] = useState("");
  const [subjectDependenciesObj, setSubjectDependenciesObj] = useState({
    subtitle: "",
    subjectDependencies: [],
  });

  const fieldRef = useRef({});

  const updateInputField = params => {
    const commonParams = {
      t,
      ...params,
      isDebounce: false,
      portalType,
      field_list: fieldList,
      isContextSelection: true,
    };

    if (isUnitDependent) {
      updateUnitPlanField({
        ...commonParams,
        ...unitPlanDetails,
      });
    } else {
      updateAssignmentField({
        ...commonParams,
        assessmentDetails,
      });
    }
  };

  const updateSubjectDependenciesObj = params => {
    setSubjectDependenciesObj(prevState => ({
      ...prevState,
      ...params,
    }));
  };

  const onClickSave = args => {
    const {
      params: argsParams,
      extraParams = {},
      shouldCloseModal = true,
      isDynamicField = false,
    } = args || {};

    // for dynamic fields with datasets, we need to update multiple
    // interdisciplinary fields at once, for unit plan AI
    if (isPlannerElementDataSet && isDynamicField) {
      _.forEach(argsParams, (value, fieldUID) => {
        // original field is only used for local state
        // no need to call mutation for it
        if (fieldUID === _.get(fields, "0.field")) {
          setContext(prevState => {
            return {
              ...prevState,
              [fieldUID]: value,
            };
          });
          return;
        }

        const params = {
          [fieldUID]: value,
        };

        updateInputField({
          params,
          extraParams,
        });
      });

      return;
    }

    _.forEach(fields, ({ field, config }) => {
      // context fields (like location, textTitles) are local and do not require any api call
      const isLocalField = _.get(config, "isLocalField", false);

      if (!isLocalField) {
        const value = _.get(argsParams || updateParams, `${field}`);

        switch (field) {
          case "unitPlan": {
            if (!getIsLearningCoursesEnabled()) {
              if (_.isArray(value) && _.isEmpty(value)) return;

              const ref = fieldRef?.current?.[field];

              if (!_.isEmpty(value)) {
                ref?.handleClearSelection?.();
              }

              setTimeout(() => {
                ref?.setSelectedUnit?.({
                  callUpdateAssessmentMutation: true,
                  updateSubjectsValue: true,
                  writeToCache: true,
                });
              });
            }

            break;
          }
          default: {
            const params = {};

            // we are not saving values in the realtime hence make a call for each subFields once user clicks save
            if (_.has(fieldList?.[field], "subFields")) {
              const subFields = _.get(fieldList[field], "subFields");
              _.forEach(subFields, subField => {
                params[`${field}_${subField.key}`] = value[subField.key];
              });
            } else {
              params[field] = value;

              const oldValue = getFieldValue({
                fields: allFields,
                uid: field,
              });

              // if value is changed then reset the dependent fields, for example if globalContextMYP is changed then reset explorationDevelopMYP value
              if (!_.isEqual(value, oldValue)) {
                const dependentFields = getDependentFields({
                  field,
                  fieldList,
                });

                if (!_.isEmpty(dependentFields)) {
                  _.forEach(dependentFields, uid => {
                    const dependentFieldValue = getFieldValue({
                      fields: allFields,
                      uid,
                    });

                    if (!_.isEmpty(dependentFieldValue)) {
                      params[uid] = _.isArray(dependentFieldValue) ? [] : null;
                    }
                  });
                }
              }
            }

            updateInputField({
              extraParams: updateParams?.extraParams || extraParams,
              params,
            });
          }
        }
      }

      setContext(prevState => {
        return {
          ...prevState,
          [field]: localContext,
        };
      });
    });

    if (shouldCloseModal) {
      onClose();
    }
  };

  const renderBody = () => {
    return _.map(fields, ({ field, config }) => {
      const style = _.get(config, "modalInnerStyle", {});

      const setUpdateParamsLocal = newValue => {
        setUpdateParams(prevValue => ({ ...prevValue, ...newValue }));
      };

      return (
        <div className={classes.container} key={field} style={style}>
          <ContextSelectionItem
            {...props}
            field={field}
            fieldConfig={config}
            fieldRef={fieldRef}
            updateParams={updateParams}
            setUpdateParams={setUpdateParamsLocal}
            localContext={localContext}
            setLocalContext={setLocalContext}
            onClickSave={onClickSave}
            subjectDependenciesObj={subjectDependenciesObj}
            updateSubjectDependenciesObj={updateSubjectDependenciesObj}
            updateInputField={updateInputField}
          />
        </div>
      );
    });
  };

  if (!shouldRenderModal) return renderBody();

  const isFieldDropdown = firstFieldType === "FieldDropdown";
  const fieldUID = _.get(fields, "0.field");

  const customStyles = getCustomModalStyle({
    styles,
    fieldType: firstFieldType,
    fieldUID,
    isUnitDependent,
  });

  const headerStyle = modalSubtext
    ? customStyles.headerWithSubtextStyle
    : customStyles.headerWithoutSubtextStyle;

  return (
    <Modal
      isOpen={true}
      shouldCloseOnOverlayClick={false}
      onClose={onClose}
      zIndex={1100}
      width={getFieldSelectionModalWidth(firstFieldType)}
      height={getFieldSelectionModalHeight({
        type: firstFieldType,
        fieldUID,
        isUnitDependent,
      })}
      {...modalStyle}
      dsVersion="2.0"
    >
      <Modal.Header style={headerStyle} isClosable={true}>
        <header className={classes.header}>
          {modalTitle ? (
            <Tooltip tooltip={modalTitle} placement={"top"} showArrow={true}>
              <div className={classes.title}>{modalTitle}</div>
            </Tooltip>
          ) : null}

          {modalSubtext ? (
            <div className={classes.subtitle}>{modalSubtext}</div>
          ) : null}
        </header>
      </Modal.Header>

      <Modal.Body style={customStyles.modalBodyStyle} isScrollable={false}>
        {renderBody()}
      </Modal.Body>

      <Modal.Footer
        hasSeparator={isFieldDropdown}
        style={customStyles.modalFooterStyle}
      >
        <footer className={classes.footer}>
          <Button
            dsVersion="2.0"
            size="medium"
            variant="neutral"
            type="outlined"
            onClick={onClose}
            testId={`${testId}-cancel`}
          >
            {t("common:cancel")}
          </Button>
          <Button
            dsVersion="2.0"
            size="medium"
            variant="progressive"
            onClick={onClickSave}
            testId={`${testId}-change-or-done`}
          >
            {t("common:done")}
          </Button>
        </footer>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    t,
    step,
    fields,
    unitPlanDetails,
    unitPlanTemplate,
    isUnitDependent,
    assessmentDetails,
    assessmentTemplate,
    assessmentTypeLabel,
    isUnitPlanAI,
  } = ownProps;

  // assessment, unit plan related data
  const { unitPlanId, unitPlanFields } = unitPlanDetails || {};
  const { allFields: assessmentFields = [] } = assessmentDetails || {};

  const { field_list: unitPlanFieldList, step_list: unitPlanStepList } =
    unitPlanTemplate || {};
  const { field_list: assessmentFieldList, step_list: assessmentStepList } =
    assessmentTemplate || {};

  const allFields = isUnitDependent
    ? _.uniqBy([...unitPlanFields, ...assessmentFields], "uid")
    : _.uniqBy([...assessmentFields, ...unitPlanFields], "uid");

  const fieldList = isUnitDependent ? unitPlanFieldList : assessmentFieldList;
  const stepList = isUnitDependent ? unitPlanStepList : assessmentStepList;

  // modal related data
  const fieldConfig = _.first(fields)?.config || {};
  const { modalSubtext: modalSubtext, modalStyle = {} } = fieldConfig || {};

  const modalTitle = getModalTitle({
    t,
    field: step || _.first(fields)?.field,
    fieldList,
    stepList,
    unitPlanFields,
    isUnitDependent,
    assessmentTypeLabel,
    fieldConfig,
    isUnitPlanAI,
  });

  const firstFieldType = getTypeToRender({
    fields,
    fieldList,
    assessmentFieldList,
    unitPlanFieldList,
    isUnitDependent,
  });

  // ELEMENT_TYPE_ADD_LEARNING_GOALS renders its own modal
  let shouldRenderModal =
    _.size(fields) >= 1 && firstFieldType !== ELEMENT_TYPE_ADD_LEARNING_GOALS;

  const isPlannerElementDataSet =
    isUnitPlanAI && firstFieldType === "PlannerElementDataSet";

  // selection of datasets (standards, atls, etc) renders its own modal
  if (isPlannerElementDataSet) {
    shouldRenderModal = false;
  }

  return {
    unitPlanId,
    assessmentFields,
    unitPlanFields,
    allFields,
    fieldList,
    stepList,
    assessmentFieldList,
    modalTitle,
    modalSubtext,
    modalStyle,
    firstFieldType,
    shouldRenderModal,
    isPlannerElementDataSet,
  };
};

const mapActionCreators = {
  updateAssignmentField,
  updateUnitPlanField,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(ContextSelectionModal);
